import React, { useMemo, useRef } from "react";
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SmartAutocomplete from "./SmartAutocomplete";
import { blue } from "@mui/material/colors";

const handleFocusSelectAll = (e) => {
  setTimeout(() => e.target.select?.(), 0);
};

const largeInputProps = {
  style: {
    fontSize: 16,
    textAlign: "center",
    height: "100%",
    paddingInline: 0,
  },
};
const largeLabelProps = {
  shrink: true,
  style: {
    fontSize: 20,
    fontWeight: 500,
    padding: 0,
  },
};

// 末尾の × ボタンを「ホバー or フォーカス中のみ表示」する共通スタイル。
// Autocomplete の clearIndicator が標準で持つ挙動を、TextField 側でも揃える。
const textFieldClearOnHoverSx = {
  width: "100%",
  padding: 0,
  height: "100%",
  "& .clear-btn": {
    visibility: "hidden",
  },
  "&:hover .clear-btn, &:focus-within .clear-btn": {
    visibility: "visible",
  },
};

/**
 * フィルタバー兼ドラッグ可能な「自由商品」フォーム。
 * 9 項目 (品名/産地/等級/階級/荷姿/重量/単位/その他規格/備考) を編集し、
 * 親側の発注表へドラッグして新規商品行を作成するために使用する。
 */
const FreeProduct = ({
  filterClearKey,
  // Filter values + setters
  filterProduct,
  setFilterProduct,
  filterOrigin,
  setFilterOrigin,
  filterRank,
  setFilterRank,
  filterProductClass,
  setFilterProductClass,
  filterPacking,
  setFilterPacking,
  filterWeight,
  setFilterWeight,
  filterWeightUnit,
  setFilterWeightUnit,
  filterOtherSpec,
  setFilterOtherSpec,
  filterRemark,
  setFilterRemark,
  // Master data
  products,
  origins,
  ranks,
  productClasses,
  packings,
  weightUnits,
  // Drag from filter bar
  filterDragRef,
  canDragFilter,
  isDraggingFilter,
}) => {
  // フィールド間移動用 ref (Enter で次のフィールドへフォーカス移動)
  const productRef = useRef(null);
  const originRef = useRef(null);
  const rankRef = useRef(null);
  const classRef = useRef(null);
  const packingRef = useRef(null);
  const weightRef = useRef(null);
  const weightUnitRef = useRef(null);
  const otherSpecRef = useRef(null);
  const remarkRef = useRef(null);

  const focusField = (ref) => {
    setTimeout(() => {
      const el = ref?.current;
      if (!el) return;
      const input = el.querySelector?.("input") || el;
      input.focus?.();
      input.select?.();
    }, 0);
  };

  // フィールド移動順 (Arrow Left/Right で移動)
  const fieldOrder = useMemo(
    () => [
      productRef,
      originRef,
      rankRef,
      classRef,
      packingRef,
      weightRef,
      weightUnitRef,
      otherSpecRef,
      remarkRef,
    ],
    [],
  );

  const navigateField = (currentRef, direction) => {
    const idx = fieldOrder.findIndex((r) => r === currentRef);
    if (idx < 0) return;
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= fieldOrder.length) return;
    focusField(fieldOrder[nextIdx]);
  };

  const makeArrowNav = (currentRef) => (direction) =>
    navigateField(currentRef, direction);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {/* Drag handle on the left */}
      <Tooltip
        title={
          canDragFilter
            ? "ドラッグして発注表に追加"
            : "ドラッグするには品名を選択してください"
        }
      >
        <Box
          ref={filterDragRef}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            alignSelf: "stretch",
            cursor: canDragFilter ? "grab" : "not-allowed",
            opacity: canDragFilter ? (isDraggingFilter ? 0.4 : 1) : 0.3,
            color: canDragFilter ? "primary.main" : "grey.400",
            "&:active": {
              cursor: canDragFilter ? "grabbing" : "not-allowed",
            }
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 28 }} />
        </Box>
      </Tooltip>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Row 1: 品名 + 産地 + 等級 + 階級 */}
        <Grid container spacing={1} alignItems="center">
          {/* 品名 */}
          <Grid item xs={4}>
            <SmartAutocomplete
              key={`product-${filterClearKey}`}
              inputRef={productRef}
              value={filterProduct}
              onChange={(_e, v) => setFilterProduct(v)}
              onSelectMoveNext={() => focusField(originRef)}
              onArrowNav={makeArrowNav(productRef)}
              options={products}
              labelKey="productName"
              idKeys={["productId"]}
              getOptionLabel={(o) => (o ? o.productName || "" : "")}
              renderOption={(props, o) => (
                <li {...props} key={o.productId}>
                  {`${o.productId} - ${o.productName}`}
                </li>
              )}
              sx={{ width: "100%", "& .MuiAutocomplete-popupIndicator": { display: "none" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="品名"
                  InputProps={{ ...params.InputProps, style: largeInputProps.style }}
                  InputLabelProps={largeLabelProps}
                  sx={{ padding: 0, height: "100%" }}
                />
              )}
            />
          </Grid>
          {/* 産地 */}
          <Grid item xs={3}>
            <SmartAutocomplete
              key={`origin-${filterClearKey}`}
              inputRef={originRef}
              value={filterOrigin}
              onChange={(_e, v) => setFilterOrigin(v)}
              onSelectMoveNext={() => focusField(rankRef)}
              onArrowNav={makeArrowNav(originRef)}
              options={origins}
              labelKey="originName"
              idKeys={["originId"]}
              getOptionLabel={(o) => (o ? o.originName || "" : "")}
              renderOption={(props, o) => (
                <li {...props} key={o.originId}>
                  {`${o.originId} - ${o.originName}`}
                </li>
              )}
              sx={{ width: "100%", "& .MuiAutocomplete-popupIndicator": { display: "none" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="産地"
                  InputProps={{ ...params.InputProps, style: largeInputProps.style }}
                  InputLabelProps={largeLabelProps}
                  sx={{ padding: 0, height: "100%" }}
                />
              )}
            />
          </Grid>
          {/* 等級 */}
          <Grid item xs={2.5}>
            <SmartAutocomplete
              key={`rank-${filterClearKey}`}
              inputRef={rankRef}
              value={filterRank}
              onChange={(_e, v) => setFilterRank(v)}
              onSelectMoveNext={() => focusField(classRef)}
              onArrowNav={makeArrowNav(rankRef)}
              options={ranks}
              labelKey="rankName"
              idKeys={["rankId"]}
              getOptionLabel={(o) => (o ? o.rankName || "" : "")}
              renderOption={(props, o) => (
                <li {...props} key={o.rankId}>
                  {`${o.rankId} - ${o.rankName}`}
                </li>
              )}
              sx={{ width: "100%", "& .MuiAutocomplete-popupIndicator": { display: "none" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="等級"
                  InputProps={{ ...params.InputProps, ...largeInputProps }}
                  InputLabelProps={largeLabelProps}
                  sx={{ padding: 0, height: "100%" }}
                />
              )}
            />
          </Grid>
          {/* 階級 */}
          <Grid item xs={2.5}>
            <SmartAutocomplete
              key={`class-${filterClearKey}`}
              inputRef={classRef}
              value={filterProductClass}
              onChange={(_e, v) => setFilterProductClass(v)}
              onSelectMoveNext={() => focusField(packingRef)}
              onArrowNav={makeArrowNav(classRef)}
              options={productClasses}
              labelKey="productClassName"
              idKeys={["productClassId"]}
              getOptionLabel={(o) => (o ? o.productClassName || "" : "")}
              renderOption={(props, o) => (
                <li {...props} key={o.productClassId}>
                  {`${o.productClassId} - ${o.productClassName}`}
                </li>
              )}
              sx={{ width: "100%", "& .MuiAutocomplete-popupIndicator": { display: "none" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="階級"
                  InputProps={{ ...params.InputProps, ...largeInputProps }}
                  InputLabelProps={largeLabelProps}
                  sx={{ padding: 0, height: "100%" }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Row 2: 荷姿 + 重量 + 単位 + その他規格 */}
        <Grid container spacing={1} alignItems="center">
          {/* 荷姿 */}
          <Grid item xs={3}>
            <SmartAutocomplete
              key={`packing-${filterClearKey}`}
              inputRef={packingRef}
              value={filterPacking}
              onChange={(_e, v) => setFilterPacking(v)}
              onSelectMoveNext={() => focusField(weightRef)}
              onArrowNav={makeArrowNav(packingRef)}
              options={packings}
              labelKey="packingName"
              idKeys={["packingId"]}
              getOptionLabel={(o) => (o ? o.packingName || "" : "")}
              renderOption={(props, o) => (
                <li {...props} key={o.packingId}>
                  {`${o.packingId} - ${o.packingName}`}
                </li>
              )}
              sx={{ width: "100%", "& .MuiAutocomplete-popupIndicator": { display: "none" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="荷姿"
                  InputProps={{ ...params.InputProps, ...largeInputProps }}
                  InputLabelProps={largeLabelProps}
                  sx={{ padding: 0, height: "100%" }}
                />
              )}
            />
          </Grid>
          {/* 重量 */}
          <Grid item xs={2.5}>
            <TextField
              key={`wt-${filterClearKey}`}
              inputRef={weightRef}
              label="重量"
              value={filterWeight}
              onChange={(e) => setFilterWeight(e.target.value)}
              onFocus={handleFocusSelectAll}
              onKeyDown={(e) => {
                const input = e.target;
                if (e.key === "Enter") {
                  e.preventDefault();
                  focusField(weightUnitRef);
                } else if (e.key === "ArrowLeft" && input.selectionStart === 0) {
                  e.preventDefault();
                  navigateField(weightRef, -1);
                } else if (e.key === "ArrowRight" && input.selectionEnd === input.value.length) {
                  e.preventDefault();
                  navigateField(weightRef, 1);
                }
              }}
              fullWidth
              InputProps={{
                ...largeInputProps,
                endAdornment: filterWeight ? (
                  <IconButton
                    className="clear-btn"
                    size="small"
                    onClick={() => setFilterWeight("")}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null,
              }}
              InputLabelProps={largeLabelProps}
              sx={textFieldClearOnHoverSx}
            />
          </Grid>
          {/* 重量単位 (g/kg) */}
          <Grid item xs={2.5}>
            <SmartAutocomplete
              key={`wtu-${filterClearKey}`}
              inputRef={weightUnitRef}
              value={filterWeightUnit}
              onChange={(_e, v) => setFilterWeightUnit(v)}
              onSelectMoveNext={() => focusField(otherSpecRef)}
              onArrowNav={makeArrowNav(weightUnitRef)}
              options={weightUnits}
              labelKey="weightUnitName"
              idKeys={["weightUnitId"]}
              getOptionLabel={(o) => (o ? o.weightUnitName || "" : "")}
              renderOption={(props, o) => (
                <li {...props} key={o.weightUnitId}>
                  {`${o.weightUnitId} - ${o.weightUnitName}`}
                </li>
              )}
              sx={{ width: "100%", "& .MuiAutocomplete-popupIndicator": { display: "none" } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="単位"
                  InputProps={{ ...params.InputProps, style: largeInputProps.style }}
                  InputLabelProps={largeLabelProps}
                  sx={{ padding: 0, height: "100%" }}
                />
              )}
            />
          </Grid>
          {/* その他規格 */}
          <Grid item xs={4}>
            <TextField
              key={`qty-${filterClearKey}`}
              inputRef={otherSpecRef}
              label="その他規格"
              value={filterOtherSpec}
              onChange={(e) => setFilterOtherSpec(e.target.value)}
              onFocus={handleFocusSelectAll}
              onKeyDown={(e) => {
                const input = e.target;
                if (e.key === "Enter") {
                  e.preventDefault();
                  focusField(remarkRef);
                } else if (e.key === "ArrowLeft" && input.selectionStart === 0) {
                  e.preventDefault();
                  navigateField(otherSpecRef, -1);
                } else if (e.key === "ArrowRight" && input.selectionEnd === input.value.length) {
                  e.preventDefault();
                  navigateField(otherSpecRef, 1);
                }
              }}
              fullWidth
              InputProps={{
                ...largeInputProps,
                endAdornment: filterOtherSpec ? (
                  <IconButton
                    className="clear-btn"
                    size="small"
                    onClick={() => setFilterOtherSpec("")}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null,
              }}
              InputLabelProps={largeLabelProps}
              sx={textFieldClearOnHoverSx}
            />
          </Grid>
        </Grid>

        {/* Row 3: 備考 (full width) */}
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <TextField
              key={`remark-${filterClearKey}`}
              inputRef={remarkRef}
              label="備考"
              value={filterRemark}
              onChange={(e) => setFilterRemark(e.target.value)}
              onFocus={handleFocusSelectAll}
              onKeyDown={(e) => {
                const input = e.target;
                if (e.key === "ArrowLeft" && input.selectionStart === 0) {
                  e.preventDefault();
                  navigateField(remarkRef, -1);
                } else if (e.key === "ArrowRight" && input.selectionEnd === input.value.length) {
                  e.preventDefault();
                  navigateField(remarkRef, 1);
                }
              }}
              fullWidth
              InputProps={{
                ...largeInputProps,
                endAdornment: filterRemark ? (
                  <IconButton
                    className="clear-btn"
                    size="small"
                    onClick={() => setFilterRemark("")}
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null,
              }}
              InputLabelProps={largeLabelProps}
              sx={textFieldClearOnHoverSx}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FreeProduct;
